import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Tabs, Tab,
    Checkbox, Typography, Chip, CircularProgress, FormControl, InputLabel,
    Select, MenuItem, Breadcrumbs, Link, Switch, FormControlLabel, Alert,
    Autocomplete, TextField,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import QuestionRender from '../../components/QuestionRender';
import api from '../../utils/v2Api';

// Import Questions picker — two ways to find source questions, one shared basket:
//   "By Test":    folder tree -> test -> its questions
//   "By Chapter": course -> chapter -> every question tagged to it (across tests)
// Selected questions are deep-COPIED into the target section (new rows on save,
// no shared state with the source). Chapter tags carry over verbatim via
// chapter_id; provenance is stamped in metadata.imported_from.
import { stripPreview, toImportPayload, chapterLabel, courseGroup, sortCoursesForPicker, contentKey, kindWord } from './importUtils';

// targetLabel: where the picks will land ("New section" / "Section 2 — QBank"),
// chosen before the modal opened — shown in the footer for confirmation.
// existingKeys: contentKey fingerprints of every question ALREADY in the test
// being edited — rows matching one get an "in test" chip before you pick them.
export default function ImportQuestionsModal({ open, onClose, onImport, colors, targetLabel = '', existingKeys }) {
    const [mode, setMode] = useState('test'); // 'test' | 'chapter'
    const [basket, setBasket] = useState(new Map()); // source question id -> {payload, label}
    const [preview, setPreview] = useState(true); // rendered question previews (default) vs compact rows
    const [sourceIsPdf, setSourceIsPdf] = useState(false); // by-test: selected test has no question UI

    // ── By Test state ──
    const [folderTree, setFolderTree] = useState([]);
    const [folderPath, setFolderPath] = useState([]); // array of folder nodes
    const [folderTests, setFolderTests] = useState([]);
    const [loadingTests, setLoadingTests] = useState(false);

    // ── By Chapter state ──
    const [courses, setCourses] = useState([]);
    const [course, setCourse] = useState('');
    const [chapters, setChapters] = useState([]);
    const [chapterId, setChapterId] = useState('');
    // '' = single chapter; set, the panel shows the UNION of every chapter
    // from chapterId..chapterIdTo (e.g. Vocab Lessons 1–21 at once).
    const [chapterIdTo, setChapterIdTo] = useState('');
    // Chapter pools default to source tests only (test_category='source');
    // this switch widens them to every test the chapter appears in.
    const [includeAll, setIncludeAll] = useState(false);

    // ── Shared question list ──
    const [rows, setRows] = useState([]); // [{q, sourceTest, group, sectionName}]
    const [rowsLabel, setRowsLabel] = useState('');
    const [loadingRows, setLoadingRows] = useState(false);

    useEffect(() => {
        if (!open) return;
        setBasket(new Map());
        setRows([]);
        setRowsLabel('');
        setSourceIsPdf(false);
        setIncludeAll(false);
        // full By-Chapter reset — stale course/chapter values otherwise LOOK
        // selected on reopen but the question panel stays empty until reselected
        setCourse('');
        setChapters([]);
        setChapterId('');
        setChapterIdTo('');
        api.get('/test-categories', { tree: true }).then((r) => setFolderTree(r.data.data || [])).catch(() => {});
        api.get('/chapters/courses').then((r) => setCourses(r.data.data || [])).catch(() => {});
        loadFolder(null, []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Switching between By Test / By Chapter must not leave the other mode's
    // stale rows on the right — clear the panel (the basket survives).
    useEffect(() => {
        setRows([]);
        setRowsLabel('');
        setSourceIsPdf(false);
        setChapterId('');
        setChapterIdTo('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    const loadFolder = async (folder, newPath) => {
        setFolderPath(newPath);
        setLoadingTests(true);
        try {
            const res = await api.get('/tests', { category_id: folder ? folder.id : 'root' });
            setFolderTests(res.data.data || []);
        } catch { setFolderTests([]); }
        setLoadingTests(false);
    };

    const currentChildren = useMemo(() => {
        let level = folderTree;
        for (const node of folderPath) {
            level = (level.find((f) => f.id === node.id) || {}).children || [];
        }
        return level;
    }, [folderTree, folderPath]);

    const loadTestQuestions = async (test) => {
        setLoadingRows(true);
        setRowsLabel(test.title);
        try {
            const res = await api.get(`/tests/${test.id}`);
            const full = res.data.data;
            setSourceIsPdf(!full.use_question_ui);
            const out = [];
            for (const s of full.v2_test_sections || []) {
                const groupsById = {};
                (s.v2_question_groups || []).forEach((g) => { groupsById[g.id] = g; });
                for (const q of s.v2_test_questions || []) {
                    out.push({
                        q, sourceTest: { id: full.id, title: full.title },
                        group: q.group_id ? groupsById[q.group_id] || null : null,
                        sectionName: s.section_name || `Section ${s.section_number}`,
                    });
                }
            }
            setRows(out);
        } catch { setRows([]); }
        setLoadingRows(false);
    };

    // Chapter ids covered by the current pick: single, or every chapter whose
    // number falls between the two ends (inclusive, either order).
    const rangeChapterIds = (fromId, toId) => {
        if (!fromId) return [];
        const sorted = [...chapters].sort((x, y) => x.number - y.number);
        if (!toId) return [fromId];
        const a = sorted.find((c) => c.id === fromId);
        const b = sorted.find((c) => c.id === toId);
        if (!a || !b) return [fromId];
        const [lo, hi] = a.number <= b.number ? [a.number, b.number] : [b.number, a.number];
        return sorted.filter((c) => c.number >= lo && c.number <= hi).map((c) => c.id);
    };

    const loadChapterQuestions = async (chId, toId = chapterIdTo, all = includeAll) => {
        setChapterId(chId);
        if (!chId) { setRows([]); return; }
        setLoadingRows(true);
        try {
            const ids = rangeChapterIds(chId, toId);
            const resps = await Promise.all(ids.map((id) =>
                api.get(`/chapters/${id}/questions`, all ? { include_all: 1 } : {})));
            const chs = resps.map((r) => r.data.data.chapter);
            const first = chs[0], last = chs[chs.length - 1];
            setRowsLabel(chs.length > 1
                ? `${first.course} — ${chapterLabel(first)}–${last.number}`
                : `${first.course} — ${chapterLabel(first)}`);
            setRows(resps.flatMap((r) => {
                const { chapter, questions } = r.data.data;
                return (questions || []).map((q) => ({
                    q: { ...q, v2_chapter: q.v2_chapter || chapter },
                    sourceTest: q.v2_test ? { id: q.v2_test.id, title: q.v2_test.title } : null,
                    group: q.v2_question_group || null,
                    sectionName: q.v2_test_section?.section_name || '',
                }));
            }));
        } catch { setRows([]); }
        setLoadingRows(false);
    };

    const toggleRow = (row) => {
        setBasket((prev) => {
            const next = new Map(prev);
            if (next.has(row.q.id)) next.delete(row.q.id);
            else next.set(row.q.id, {
                payload: toImportPayload(row.q, row.sourceTest, row.group),
                label: `${row.sourceTest?.title || ''} Q${row.q.question_number}`,
            });
            return next;
        });
    };

    const allSelected = rows.length > 0 && rows.every((r) => basket.has(r.q.id));
    const toggleAll = () => {
        setBasket((prev) => {
            const next = new Map(prev);
            if (allSelected) rows.forEach((r) => next.delete(r.q.id));
            else rows.forEach((r) => {
                if (!next.has(r.q.id)) next.set(r.q.id, {
                    payload: toImportPayload(r.q, r.sourceTest, r.group),
                    label: `${r.sourceTest?.title || ''} Q${r.q.question_number}`,
                });
            });
            return next;
        });
    };

    const handleImport = () => {
        onImport([...basket.values()].map((v) => v.payload));
    };

    const rowSx = { display: 'flex', alignItems: 'center', gap: 1, py: 0.25, px: 0.5, borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } };
    // same layout without its own hover/cursor — used inside the clickable card wrapper
    const lineSx = { display: 'flex', alignItems: 'center', gap: 1, py: 0.25, px: 0.5 };

    // Basket recap — shown in BOTH tabs' left panels: review picks from other
    // courses/tests and remove them without navigating back. The inner list is
    // the only scrolling element (wrapper is height-capped by the caller's sx).
    const renderBasketRecap = (wrapperSx) => {
        if (basket.size === 0) return null;
        // content twins INSIDE the selection (same text picked from two tests)
        const keyCounts = new Map();
        for (const b of basket.values()) {
            const k = contentKey(b.payload);
            if (k) keyCounts.set(k, (keyCounts.get(k) || 0) + 1);
        }
        return (
        <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 1, display: 'flex', flexDirection: 'column', minHeight: 0, ...wrapperSx }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ flexShrink: 0 }}>
                Selected ({basket.size})
            </Typography>
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {[...basket.entries()].map(([id, b]) => {
                    const p = b.payload;
                    const k = contentKey(p);
                    let answerText = p.answer || '';
                    if (!answerText && p.accepted_answers) {
                        try { answerText = JSON.parse(p.accepted_answers).join(' / '); } catch { /* raw string */ }
                    }
                    const chipSx = { height: 18, fontSize: 10, flexShrink: 0 };
                    return (
                        <Box key={id} sx={{ borderBottom: 1, borderColor: 'divider', pb: 0.75 }}>
                            <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                                <IconButton size="small" sx={{ p: 0.25 }} title="Remove from selection"
                                    onClick={() => setBasket((prev) => { const n = new Map(prev); n.delete(id); return n; })}>
                                    <CloseIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                                <Chip label={p.question_type} size="small" sx={chipSx} />
                                {p._chapter_course && p._loaded_chapter_number && (
                                    <>
                                        <Chip label={p._chapter_course} size="small" variant="outlined" sx={chipSx} />
                                        <Chip label={p._chapter_kind === 'difficulty'
                                                ? (p._chapter_label || `Level ${p._loaded_chapter_number}`)
                                                : `${p._chapter_kind === 'lesson' ? 'Lesson' : 'Ch'} ${p._loaded_chapter_number}`}
                                            size="small" color="info" variant="outlined" sx={chipSx} />
                                    </>
                                )}
                                {p._source_group && <Chip label="passage" size="small" variant="outlined" sx={chipSx}
                                    title="Belongs to a reading passage (stimulus group)" />}
                                {answerText && <Chip label={answerText} size="small" title="Correct answer"
                                    sx={{ ...chipSx, fontWeight: 700, bgcolor: 'rgba(81,190,144,0.2)' }} />}
                                {k && keyCounts.get(k) > 1 && (
                                    <Chip label="dup in selection" size="small" color="error" variant="outlined"
                                        title="Two selected questions have identical text — remove one"
                                        sx={{ ...chipSx, fontWeight: 700 }} />
                                )}
                                {k && existingKeys?.has(k) && (
                                    <Chip label="in test" size="small" color="warning" variant="outlined"
                                        title="A question with this exact text is already in the test you're editing"
                                        sx={{ ...chipSx, fontWeight: 700 }} />
                                )}
                            </Box>
                            {/* rendered like preview mode: latex + images */}
                            <Box sx={{ ml: 1, mt: 0.25, pl: 1, borderLeft: 2, borderColor: 'divider',
                                fontSize: 13, '& img': { maxWidth: '100%', height: 'auto' } }}>
                                {p.question_prompt
                                    ? <QuestionRender content={p.question_prompt} />
                                    : <Typography variant="caption" color="text.secondary">[no question text]</Typography>}
                            </Box>
                            <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ fontSize: 10, ml: 1 }}>
                                {b.label}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Box>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle sx={{ pb: 0 }}>
                Import Questions
                <Tabs value={mode} onChange={(e, v) => setMode(v)} sx={{ mt: 1 }}>
                    <Tab label="By Test" value="test" />
                    <Tab label="By Chapter" value="chapter" />
                </Tabs>
            </DialogTitle>
            <DialogContent dividers sx={{ display: 'flex', gap: 2, height: '65vh' }}>
                {/* Left: source browser */}
                <Box sx={{ width: 320, flexShrink: 0, overflowY: 'auto', borderRight: 1, borderColor: 'divider', pr: 2 }}>
                    {mode === 'test' ? (
                        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                                <Breadcrumbs sx={{ mb: 1, fontSize: 13 }}>
                                    <Link component="button" underline="hover" onClick={() => loadFolder(null, [])}>Library</Link>
                                    {folderPath.map((f, i) => (
                                        <Link key={f.id} component="button" underline="hover"
                                            onClick={() => loadFolder(f, folderPath.slice(0, i + 1))}>
                                            {f.name}
                                        </Link>
                                    ))}
                                </Breadcrumbs>
                                {currentChildren.map((f) => (
                                    <Box key={`f${f.id}`} sx={rowSx} onClick={() => loadFolder(f, [...folderPath, f])}>
                                        <FolderIcon fontSize="small" sx={{ color: 'warning.main' }} />
                                        <Typography variant="body2" noWrap>{f.name}</Typography>
                                    </Box>
                                ))}
                                {loadingTests ? <CircularProgress size={18} sx={{ m: 1 }} /> : folderTests.map((t) => (
                                    <Box key={`t${t.id}`} sx={{ ...rowSx, bgcolor: rowsLabel === t.title ? 'action.selected' : undefined }}
                                        onClick={() => loadTestQuestions(t)}>
                                        <DescriptionIcon fontSize="small" sx={{ color: 'info.main' }} />
                                        <Typography variant="body2" noWrap>{t.title}</Typography>
                                    </Box>
                                ))}
                            </Box>
                            {/* recap capped below the tree — tree keeps at least half the panel */}
                            {renderBasketRecap({ flex: '0 1 auto', maxHeight: '45%', mt: 1 })}
                        </Box>
                    ) : (
                        <Box sx={{ height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                            <Autocomplete
                                size="small" fullWidth
                                options={sortCoursesForPicker([...new Set(courses.map((c) => c.course))])}
                                groupBy={courseGroup}
                                value={course || null}
                                onChange={async (e, c) => {
                                    setCourse(c || ''); setChapterId(''); setChapterIdTo(''); setRows([]);
                                    if (!c) { setChapters([]); return; }
                                    try {
                                        const r = await api.get('/chapters', { course: c });
                                        setChapters(r.data.data || []);
                                    } catch { setChapters([]); }
                                }}
                                renderInput={(params) => <TextField {...params} label="Course" />}
                            />
                            <Autocomplete
                                size="small" fullWidth disabled={!course}
                                options={[...chapters].sort((a, b) => a.number - b.number)}
                                getOptionLabel={(ch) => `${chapterLabel(ch)}${ch.label && ch.kind !== 'difficulty' ? ` — ${ch.label}` : ''}`}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                value={chapters.find((c) => c.id === chapterId) || null}
                                onChange={(e, ch) => { setChapterIdTo(''); loadChapterQuestions(ch ? ch.id : '', ''); }}
                                renderInput={(params) => <TextField {...params} label={kindWord(courses.find((c) => c.course === course)?.kind)} />}
                            />
                            <Autocomplete
                                size="small" fullWidth disabled={!chapterId}
                                options={(() => {
                                    const start = chapters.find((c) => c.id === chapterId);
                                    return [...chapters].sort((a, b) => a.number - b.number)
                                        .filter((ch) => start && ch.number > start.number);
                                })()}
                                getOptionLabel={(ch) => chapterLabel(ch)}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                value={chapters.find((c) => c.id === chapterIdTo) || null}
                                onChange={(e, ch) => { const v = ch ? ch.id : ''; setChapterIdTo(v); loadChapterQuestions(chapterId, v); }}
                                renderInput={(params) => <TextField {...params} label="to (range)" placeholder="single" />}
                            />
                            <FormControlLabel
                                sx={{ mr: 0, ml: -0.5 }}
                                control={<Switch size="small" checked={includeAll}
                                    onChange={(e) => { setIncludeAll(e.target.checked); if (chapterId) loadChapterQuestions(chapterId, chapterIdTo, e.target.checked); }} />}
                                label={<Typography variant="caption">Include non-source tests (exam copies)</Typography>}
                            />
                            {renderBasketRecap({ flex: 1 })}
                        </Box>
                    )}
                </Box>

                {/* Right: question list */}
                <Box sx={{ flex: 1, overflowY: 'auto', minWidth: 0 }}>
                    {rows.length > 0 && (
                        <Box sx={{ ...lineSx, borderBottom: 1, borderColor: 'divider', mb: 0.5, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 2 }}>
                            <Checkbox size="small" checked={allSelected} onClick={toggleAll} sx={{ p: 0.5 }} />
                            <Typography variant="body2" fontWeight={600} onClick={toggleAll} sx={{ cursor: 'pointer', flex: 1, minWidth: 0 }} noWrap>
                                {rowsLabel} — {rows.length} questions
                            </Typography>
                            <FormControlLabel
                                sx={{ mr: 0, flexShrink: 0 }}
                                control={<Switch size="small" checked={preview} onChange={(e) => setPreview(e.target.checked)} />}
                                label={<Typography variant="caption">Preview</Typography>}
                            />
                        </Box>
                    )}
                    {preview && sourceIsPdf && rows.length > 0 && (
                        <Alert severity="info" sx={{ mb: 1 }}>
                            This test is PDF-based — question previews are unavailable (only the answer key is stored).
                        </Alert>
                    )}
                    {loadingRows && <Box display="flex" justifyContent="center" mt={4}><CircularProgress size={24} /></Box>}
                    {!loadingRows && rows.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
                            {mode === 'test'
                                ? 'Nothing selected yet — pick a test on the left to list its questions.'
                                : 'Nothing selected yet — pick a course and chapter on the left.'}
                        </Typography>
                    )}
                    {rows.map((row) => {
                        const hasContent = row.q.question_prompt || (row.q.v2_question_options || []).some((o) => o.option_text);
                        const correct = new Set(String(row.q.answer || '').split(/[,|]/).map((s) => s.trim().toUpperCase()).filter(Boolean));
                        const answerText = row.q.answer
                            || (Array.isArray(row.q.accepted_answers) ? row.q.accepted_answers.join(' / ') : '');
                        return (
                        // the WHOLE card toggles selection — forgiving click target in both modes
                        <Box key={row.q.id} onClick={() => toggleRow(row)}
                            sx={{ mb: preview ? 1 : 0, borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
                            <Box sx={lineSx}>
                                <Checkbox size="small" checked={basket.has(row.q.id)} sx={{ p: 0.5 }} />
                                <Typography variant="caption" sx={{ width: 34, flexShrink: 0, fontWeight: 700 }}>Q{row.q.question_number}</Typography>
                                <Chip label={row.q.question_type} size="small" sx={{ height: 18, fontSize: 10, flexShrink: 0 }} />
                                {row.q.v2_chapter && (
                                    <>
                                        <Chip label={row.q.v2_chapter.course} size="small" variant="outlined"
                                            sx={{ height: 18, fontSize: 10, flexShrink: 0 }} />
                                        <Chip label={chapterLabel(row.q.v2_chapter)} size="small" color="info" variant="outlined"
                                            sx={{ height: 18, fontSize: 10, flexShrink: 0 }} />
                                    </>
                                )}
                                {row.group && <Chip label="passage" size="small" variant="outlined" title="Belongs to a reading passage (stimulus group)" sx={{ height: 18, fontSize: 10, flexShrink: 0 }} />}
                                {existingKeys?.has(contentKey(row.q)) && (
                                    <Chip label="in test" size="small" color="warning" variant="outlined"
                                        title="A question with this exact text is already in the test you're editing"
                                        sx={{ height: 18, fontSize: 10, flexShrink: 0, fontWeight: 700 }} />
                                )}
                                {!preview && (
                                    <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                                        {stripPreview(row.q.question_prompt) || <em>(no prompt)</em>}
                                    </Typography>
                                )}
                                {!preview && answerText && (
                                    <Chip label={answerText} size="small"
                                        title="Correct answer"
                                        sx={{ height: 18, fontSize: 10, flexShrink: 0, fontWeight: 700, bgcolor: 'rgba(81,190,144,0.2)' }} />
                                )}
                                <Box sx={{ flex: preview ? 1 : 0 }} />
                                {mode === 'chapter' && row.sourceTest && (
                                    <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 180, flexShrink: 0 }}>
                                        {row.sourceTest.title}
                                    </Typography>
                                )}
                            </Box>
                            {/* Preview mode: render the question the way it's displayed */}
                            {preview && (
                                <Box sx={{ ml: 5, mr: 1, pb: 1, pl: 1.5, borderLeft: 2, borderColor: 'divider' }}>
                                    {hasContent ? (
                                        <>
                                            {row.q.question_prompt && <QuestionRender content={row.q.question_prompt} />}
                                            {(row.q.v2_question_options || []).map((o) => {
                                                const isCorrect = correct.has(String(o.option_label || '').toUpperCase());
                                                return (
                                                    <Box key={o.id} display="flex" gap={1} alignItems="center" sx={{ py: 0.1 }}>
                                                        {isCorrect
                                                            ? <CheckCircleIcon sx={{ fontSize: 14, color: '#51BE90', flexShrink: 0 }} />
                                                            : <Box sx={{ width: 14, flexShrink: 0 }} />}
                                                        <Typography variant="body2" fontWeight={isCorrect ? 700 : 400} sx={{ flexShrink: 0 }}>{o.option_label}.</Typography>
                                                        {o.option_text && <QuestionRender content={o.option_text} />}
                                                    </Box>
                                                );
                                            })}
                                        </>
                                    ) : (
                                        <Typography variant="caption" color="text.secondary">
                                            No question content stored — preview unavailable (answer: {answerText || '—'})
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Box>
                        );
                    })}
                </Box>
            </DialogContent>
            <DialogActions>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto', ml: 1 }}>
                    {basket.size} selected{targetLabel ? <> → <b>{targetLabel}</b></> : ''}
                </Typography>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" color="secondary" disabled={basket.size === 0} onClick={handleImport}>
                    Import {basket.size > 0 ? basket.size : ''} Question{basket.size === 1 ? '' : 's'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
